import { FC, useEffect, useState } from "react";
import { CarDataType } from "data/types";
import { useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import { CarListingModal } from "models/vehicleModal";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Heading from "shared/Heading/Heading";
import FleetCard from "designs/DesignA/components/FleetCard/FleetCard";
import TabFilters from "../../../Common/TabFilters";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: CarDataType[];
}

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data,
}) => {
  const { ourVehicles, ourVehiclesLoading, setSelectedCar } = useData();
  const [visibleCarsCount, setVisibleCarsCount] = useState<number>(8);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedMakes, setSelectedMakes] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<string>("default");
  const [maxPrice, setMaxPrice] = useState(0);
  const [filteredFleet, setFilteredFleet] = useState<CarListingModal[]>(
    ourVehicles || []
  );
  const [rangePrices, setRangePrices] = useState([0, maxPrice]);
  const navigate = useNavigate();

  useEffect(() => {
    if (ourVehicles && ourVehicles.length > 0) {
      const calculatedMaxPrice = ourVehicles.reduce(
        (acc, current) => Math.max(acc, current.avg_car_price_per_day ?? 0),
        0
      );
      setMaxPrice(calculatedMaxPrice);
    }
  }, [ourVehicles]);

  useEffect(() => {
    if (maxPrice > 0) {
      setRangePrices([0, maxPrice]);
    }
  }, [maxPrice]);

  const getUniqueFleetClasses = () => {
    return Array.from(
      new Set(ourVehicles?.map((car) => car?.fleet_class?.name ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const getUniqueMakes = () => {
    return Array.from(
      new Set(ourVehicles?.map((car) => car?.make ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const getUniqueModels = () => {
    return Array.from(
      new Set(ourVehicles?.map((car) => car?.model ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const hasMakeData = () => {
    return ourVehicles?.some((car) => car?.make && car.make !== "") || false;
  };

  const hasModelData = () => {
    return ourVehicles?.some((car) => car?.model && car.model !== "") || false;
  };

  useEffect(() => {
    if (ourVehicles) {
      let filtered = [...ourVehicles];
      filtered = filtered.filter(
        (item) =>
          (item.avg_car_price_per_day ?? 0) >= rangePrices[0] &&
          (item.avg_car_price_per_day ?? 0) <= rangePrices[1]
      );


      if (selectedTypes.length > 0) {
        const totalTypes = getUniqueFleetClasses().length;
        if (selectedTypes.length === totalTypes) {
          filtered = filtered.filter(
            (item: CarListingModal) => (item?.fleet_class?.name ?? "").trim() !== ""
          );
        } else {
          filtered = filtered.filter((item: CarListingModal) =>
            selectedTypes.includes(item?.fleet_class?.name ?? "")
          );
        }
      }

      if (selectedMakes.length > 0) {
        const totalMakes = getUniqueMakes().length;
        if (selectedMakes.length === totalMakes) {
          filtered = filtered.filter(
            (item: CarListingModal) => (item?.make ?? "").trim() !== ""
          );
        } else {
          filtered = filtered.filter((item: CarListingModal) =>
            selectedMakes.includes(item?.make ?? "")
          );
        }
      }


      if (selectedModels.length > 0) {
        const totalModels = getUniqueModels().length;
        if (selectedModels.length === totalModels) {
          filtered = filtered.filter(
            (item: CarListingModal) => (item?.model ?? "").trim() !== ""
          );
        } else {
          filtered = filtered.filter((item: CarListingModal) =>
            selectedModels.includes(item?.model ?? "")
          );
        }
      }

      if (sortOrder === "aToZ") {
        filtered.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      } else if (sortOrder === "zToA") {
        filtered.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameB.localeCompare(nameA);
        });
      } else if (sortOrder === "priceLowToHigh") {
        filtered.sort(
          (a, b) =>
            (a.avg_car_price_per_day ?? 0) - (b.avg_car_price_per_day ?? 0)
        );
      } else if (sortOrder === "priceHighToLow") {
        filtered.sort((a, b) => (b.avg_car_price_per_day ?? 0) - (a.avg_car_price_per_day ?? 0));
      }

      setFilteredFleet(filtered);

      if (filtered.length <= visibleCarsCount) {
        setExpanded(false);
      }
    }
  }, [
    ourVehicles,
    selectedTypes,
    selectedMakes,
    selectedModels,
    rangePrices,
    sortOrder,
  ]);

  const renderCard = (car: CarListingModal) => {
    return (
      <FleetCard
        key={car.id}
        data={car}
        onClick={() => {
          navigate("/car-listing-detail?selectedCarId=" + car.id);
          setSelectedCar(car);
        }}
      />
    );
  };
  const carsToDisplay = filteredFleet?.slice(0, visibleCarsCount);
  const shouldCenter = carsToDisplay && carsToDisplay.length < 4;

  const handleShowMore = () => {
    setVisibleCarsCount((prevCount) => prevCount + 8);
    if (filteredFleet && visibleCarsCount + 8 >= filteredFleet.length) {
      setExpanded(true);
    }
  };

  const handleShowLess = () => {
    setVisibleCarsCount(8);
    setExpanded(false);
  };

  return (
    <div className="nc-SectionGridFeaturePlaces relative">
      <div className="flex flex-col mb-8 relative">
        <div className="flex justify-between">
          <div className="flex flex-col gap-1">
            <Heading>Explore All Vehicles</Heading>
            {!ourVehiclesLoading &&
              filteredFleet !== undefined &&
              (selectedTypes.length > 0 ||
                selectedMakes.length > 0 ||
                selectedModels.length > 0 ||
                rangePrices[0] > 0 ||
                rangePrices[1] < maxPrice ||
                sortOrder !== "default") && (
                <span className="inline-flex items-center gap-1.5 text-sm mt-2 font-medium text-button-color bg-primary-100 px-3 py-1 rounded-full w-fit shadow-md">
                  {filteredFleet.length} {filteredFleet.length === 1 ? "Vehicle" : "Vehicles"} Found
                </span>
              )}
          </div>
          {ourVehicles && (
            <TabFilters
              types={[...getUniqueFleetClasses()]}
              makes={hasMakeData() ? [...getUniqueMakes()] : []}
              models={hasModelData() ? [...getUniqueModels()] : []}
              selectedTypes={selectedTypes}
              selectedMakes={selectedMakes}
              selectedModels={selectedModels}
              setSelectedTypes={setSelectedTypes}
              setSelectedMakes={setSelectedMakes}
              setSelectedModels={setSelectedModels}
              rangePrices={rangePrices}
              setRangePrices={setRangePrices}
              maxPrice={maxPrice}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              showMakeFilter={hasMakeData()}
              showModelFilter={hasModelData()}
            />
          )}
        </div>
      </div>
      <div
        className={`${shouldCenter
            ? "flex flex-wrap justify-center gap-6 md:gap-8 px-4 sm:px-8 md:px-12 lg:px-12"
            : "grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          }`}
      >
        {ourVehiclesLoading ? (
          <div className="flex justify-center items-center w-full">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-t-transparent border-secondary-100"></div>
          </div>
        ) : (
          carsToDisplay?.map((car) => renderCard(car))
        )}
      </div>
      {!expanded && filteredFleet?.length > visibleCarsCount && (
        <div className="flex mt-16 justify-center items-center">
          <ButtonPrimary className="rounded-md" onClick={handleShowMore}>
            View more
          </ButtonPrimary>
        </div>
      )}
      {expanded && filteredFleet?.length > 8 && (
        <div className="flex mt-16 justify-center items-center">
          <ButtonPrimary className="rounded-md" onClick={handleShowLess}>
            View less
          </ButtonPrimary>
        </div>
      )}
    </div>
  );
};

export default SectionGridFilterCard;
